/**
 * useTheme Hook
 * Phase 5: Code Quality - Remove duplicate theme detection logic
 *
 * BEFORE: `const isDark = theme === 'dark';` duplicated 30+ times
 * AFTER: `const { theme, isDark } = useTheme();` centralized logic
 */

import { useUIStore } from '@atlas-forms/state-react';

export interface UseThemeReturn {
  /** Current theme mode ('light' or 'dark') */
  theme: 'light' | 'dark';

  /** Whether dark mode is active */
  isDark: boolean;

  /** Whether light mode is active */
  isLight: boolean;

  /** Toggle between light and dark modes */
  toggleTheme: () => void;

  /** Set specific theme */
  setTheme: (theme: 'light' | 'dark') => void;
}

/**
 * Custom hook for theme access and manipulation
 *
 * Provides theme information and utilities without duplicating
 * the `isDark` calculation across components.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { theme, isDark, toggleTheme } = useTheme();
 *
 *   return (
 *     <div data-theme={theme}>
 *       <button onClick={toggleTheme}>
 *         Switch to {isDark ? 'light' : 'dark'} mode
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useTheme(): UseThemeReturn {
  const { theme, setTheme } = useUIStore();

  const isDark = theme === 'dark';
  const isLight = theme === 'light';

  const toggleTheme = () => {
    setTheme(isDark ? 'light' : 'dark');
  };

  return {
    theme,
    isDark,
    isLight,
    toggleTheme,
    setTheme,
  };
}

export default useTheme;
